import React, { useEffect, useState } from 'react';
import { Link } from 'gatsby-link';
import styled, { useTheme } from 'styled-components';

import {
  BUTTON_THEMES,
  BUTTON_TYPES,
  ICON_TYPES,
} from '../../../shared/constants';
import { VIEW_TYPES } from './shared/constants';
import { INavigationItem } from './shared/types';

import DropShadow from '../../atoms/drop-shadow/DropShadow';
import TriangleIconLogo from '../../atoms/icons/TriangleIconLogo';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { useWindowDimensions } from '../../../utils/generic';

import mq, { breakpoints } from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { Body } from '../../../styles/generic';
import { ThemeName } from '../../../styles/themes';

import StaticContent from '../../../data/static-content/navigation/static-content.json';
import bisLogo from '../../../images/bis-logo.svg';
import defaultLogo from '../../../images/default-logo.svg';
import { Widths } from '../../../tokens';

import DesktopDropdownMenu from './DesktopDropdownMenu';
import DesktopNavigation from './DesktopNavigation';
import MobileDropdownMenu from './MobileDropdownMenu';
import MobileNavigation from './MobileNavigation';
import { NavigationProvider } from './NavigationContextProvider';

const navStaticContent = StaticContent.navigation;
const intakeSteps: { [key: string]: string } = {
  buttonCopy: 'Krijg relevante content',
  url: '/groeiplan-op-maat',
};

const LINKS = {
  abnamro: {
    label: 'Naar abnamro.nl',
    url: 'https://www.abnamro.nl/',
  },
  doorpakken: {
    label: 'Doorpakken',
    url: '/',
  },
};

interface Props {
  disableMenu?: boolean;
}

const Navigation = ({ disableMenu = false }: Props) => {
  const { width } = useWindowDimensions();
  const [viewType, setViewType] = useState<string>();
  const [activeTopItem, setActiveTopItem] = useState<
    INavigationItem | undefined
  >();

  useEffect(() => {
    if (width) {
      setViewType(
        width >= breakpoints.M ? VIEW_TYPES.desktop : VIEW_TYPES.mobile
      );
    }
  }, [width]);

  const handleOnRequestClose = () => {
    setActiveTopItem(undefined);
  };

  const renderDropDown = () => (
    <DropdownWrapper isDesktopView={viewType === VIEW_TYPES.desktop}>
      <DropdownContainer>
        {viewType === VIEW_TYPES.desktop ? (
          <DesktopDropdownMenu
            onRequestCloseHandler={handleOnRequestClose}
            activeTopItem={activeTopItem as INavigationItem}
          />
        ) : (
          <MobileDropdownMenu
            onRequestCloseHandler={handleOnRequestClose}
            activeTopItem={activeTopItem as INavigationItem}
          />
        )}
      </DropdownContainer>
    </DropdownWrapper>
  );

  const renderHeaderLinks = () => (
    <HeaderContainer>
      <Body hasCustomLinkStyles>
        <HeaderLink
          target="_blank"
          href={LINKS.abnamro.url}
          data-element-type={DEQ_TYPES.textLink}
          data-element-label={LINKS.abnamro.label.toLowerCase()}
          data-component-type={DEQ_TYPES.topNavigation}
          data-element-target={LINKS.abnamro.url}
        >
          {LINKS.abnamro.label}
        </HeaderLink>
        <HeaderLink
          active
          href={LINKS.doorpakken.url}
          data-element-type={DEQ_TYPES.textLink}
          data-component-type={DEQ_TYPES.topNavigation}
          data-element-label={navStaticContent.navTitle.toLowerCase()}
          data-element-target={LINKS.doorpakken.url}
        >
          {navStaticContent.navTitle}
        </HeaderLink>
      </Body>
    </HeaderContainer>
  );

  const renderLogo = () => {
    const { name } = useTheme();

    return (
      <LogoLink
        to={LINKS.doorpakken.url}
        data-element-type={DEQ_TYPES.image}
        data-element-label={DEQ_TYPES.logo.toLowerCase()}
        data-component-type={DEQ_TYPES.topNavigation}
        data-element-target={LINKS.doorpakken.url}
      >
        <LogoImage
          src={name === ThemeName.DEFAULT ? defaultLogo : bisLogo}
          alt={LINKS.doorpakken.label}
        />
        <XLContainer>
          <TriangleWrapper>
            <TriangleIconLogo />
          </TriangleWrapper>
        </XLContainer>
      </LogoLink>
    );
  };

  const renderDesktopHeader = () => (
    <DesktopContainer>
      <TopHeader>
        {renderLogo()}
        {!disableMenu && (
          <>
            {renderHeaderLinks()}
            <StyledPrimaryButton
              key="prim-btn-intake"
              type={BUTTON_TYPES.primary}
              label={intakeSteps.buttonCopy}
              buttonTheme={BUTTON_THEMES.highlight}
              to={intakeSteps.url}
              icon={ICON_TYPES.none}
              deqCustomAttributes={{ componentType: DEQ_TYPES.topNavigation }}
            />
          </>
        )}
      </TopHeader>
    </DesktopContainer>
  );

  const renderMobileHeader = () => (
    <MobileContainer>
      {!disableMenu && renderHeaderLinks()}
      <BottomRowHeader>
        {renderLogo()}
        {!disableMenu && (
          <StyledSecondaryButton
            key="prim-btn-intake"
            type={BUTTON_TYPES.primary}
            label={intakeSteps.buttonCopy}
            buttonTheme={BUTTON_THEMES.default}
            to={intakeSteps.url}
            icon={ICON_TYPES.none}
            deqCustomAttributes={{ componentType: DEQ_TYPES.topNavigation }}
          />
        )}
      </BottomRowHeader>
    </MobileContainer>
  );

  return (
    <NavigationProvider>
      <DropShadow
        viewType={viewType}
        onRequestClose={handleOnRequestClose}
        show={Boolean(activeTopItem)}
      >
        <NavigationWrapper data-component-type="NavigationWrapper">
          <Container
            data-component-type="NavigationContainer"
            data-content-group-type={DEQ_TYPES.container}
            data-content-group-title="navigation menu"
          >
            {renderDesktopHeader()}
            {renderMobileHeader()}
          </Container>
          {!disableMenu && (
            <TopBarBackground>
              <MobileContainer>
                <MobileNavigation
                  activeTopItem={activeTopItem}
                  onItemClick={(activeItem) => {
                    setActiveTopItem(
                      activeItem === activeTopItem ? undefined : activeItem
                    );
                  }}
                />
              </MobileContainer>
              <DesktopContainer>
                <DesktopNavigation
                  activeTopItem={activeTopItem}
                  onItemClick={(activeItem) => {
                    setActiveTopItem(
                      activeItem === activeTopItem ? undefined : activeItem
                    );
                  }}
                />
              </DesktopContainer>
            </TopBarBackground>
          )}
          {activeTopItem && renderDropDown()}
        </NavigationWrapper>
      </DropShadow>
    </NavigationProvider>
  );
};

export default Navigation;

const TopHeader = styled.div`
  align-items: center;
  display: flex;
  width: 100%;
`;

const XLContainer = styled.div`
  display: none;

  ${mq.from.breakpoint.L`
    display: ${({ theme }) =>
      theme.name === ThemeName.DEFAULT ? 'block' : 'none'};
    width: 100%;
  `}
`;

const DesktopContainer = styled.div`
  display: none;

  ${mq.from.breakpoint.M`        
    display: block;
    width: 100%;
  `}
`;

const MobileContainer = styled.div`
  display: block;

  ${mq.from.breakpoint.M`        
    display: none;
  `}
`;

const NavigationWrapper = styled.div`
  background-color: white;
  font-size: 16px;
  position: relative;
  z-index: 6;
`;

const LogoLink = styled(Link)`
  position: relative;
`;

const HeaderLink = styled.a<{ active?: boolean }>`
  color: ${({ active, theme }) =>
    active ? theme.colors.abnGreen400 : Colors.abnNeutral400};
  display: inline-block;
  font-weight: ${({ active }) => (active ? 500 : 400)};
  line-height: 20px;
  padding-right: 12px;
  position: relative;
  text-decoration: none;

  &:not(:last-of-type) {
    border-right: 1px solid ${Colors.abnNeutralsN65};
    margin-right: 12px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.abnGreen300};
  }
`;

const DropdownWrapper = styled.div<{ isDesktopView: boolean }>`
  left: 0;
  max-height: 50%;
  padding-bottom: 14px;
  position: absolute;
  width: 100%;
  z-index: 10;

  ${({ isDesktopView }) =>
    !isDesktopView ? `height: calc(100vh - 112px);` : ''}
`;

const DropdownContainer = styled.div`
  background-color: ${Colors.abnGreen500};
  color: ${Colors.white};
  display: flex;
  width: 100%;
  z-index: 50;
`;

const Container = styled.div`
  background: ${Colors.white};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.abnGreen300};
  display: flex;
  flex-direction: column;
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: 10px;
  justify-content: flex-start;
  position: relative;

  ${mq.from.breakpoint.M`        
    padding: 0 1.5rem;
    align-items: center;
    height: 4rem;
    margin: 0 auto;
    flex-direction: row;
    max-width: ${Widths.PAGE_MAX};
  `}
`;

const HeaderContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.abnNeutral50};
  display: inline-flex;
  gap: 12px;
  padding: 12px 16px;
  width: calc(100% - 32px);

  ${mq.from.breakpoint.M`        
    justify-content: flex-end;
    background-color: transparent;
    margin: 0 16px 0 0;
    flex-grow: 1;
    width: auto;
  `};
`;

const LogoImage = styled.img`
  font-size: 0.875rem;
  height: 32px;
  height: 2rem;
  margin: 12px 16px;
  position: relative;

  ${mq.from.breakpoint.M`
    margin: 0.75rem 0 0;
  `}
`;

const TopBarBackground = styled.div`
  background-color: ${({ theme }) => theme.navigation.topBar.background};
  left: 0;
  margin: auto;
  overflow-x: scroll;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;
  width: 100%;
  z-index: 5;
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  border: none;
  &:hover:not(&:disabled) {
    border: none;
  }
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  padding: 0 16px;
`;

const TriangleWrapper = styled.div`
  bottom: -27px;
  display: block;
  left: -5px;
  position: absolute;
  z-index: 20;
`;

const BottomRowHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
